import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportTaxData,
  TReportTaxParams,
} from '@src/types/reports';
import { downloadFile } from '@src/lib/utils';
import { exportTaxReport, getTaxReport } from '@src/services/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const TaxReport: FC = () => {
  // Data state
  const [data, setData] = useState<TReportTaxData[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: ['tax_amount'],
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, _) => item.tax_amount,
    getPreviousPeriodField: (item, _) => item.tax_amount_previous,
    getPreviousYearField: (item, _) => item.tax_amount_previous_year,
    includeTotalsRow: true,
    valueFormatter: (_, value: number) => `$${value.toLocaleString()}`,
    columnTitleFormatter: (key) => {
      if (key === 'tax_amount') return 'Tax Amount, USD';
      if (key === 'tax_amount_previous')
        return 'Tax Amount, USD (Previous Period)';
      if (key === 'tax_amount_previous_year')
        return 'Tax Amount, USD (Previous Year)';

      return key;
    },
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, _) => item.tax_amount,
    getPreviousPeriodField: (item, _) => item.tax_amount_previous,
    getPreviousYearField: (item, _) => item.tax_amount_previous_year,
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const params: TReportTaxParams = {
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
        compare_to_previous_period: filters.compareToPreviousPeriod,
        compare_to_same_period_previous_year:
          filters.compareToSamePeriodPreviousYear,
      };

      const response = await getTaxReport(params);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async (format: 'csv' | 'xlsx') => {
    filters.setIsLoading(true);
    try {
      const payload: TReportTaxParams = {
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
        compare_to_previous_period: filters.compareToPreviousPeriod,
        compare_to_same_period_previous_year:
          filters.compareToSamePeriodPreviousYear,
      };

      const response = await exportTaxReport(payload, format);

      if (format === 'csv') {
        const csvBlob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        });
        const csvUrl = URL.createObjectURL(csvBlob);
        const link = document.createElement('a');

        link.href = csvUrl;
        link.download = 'tax_report.csv';
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(csvUrl);
        document.body.removeChild(link);
      } else {
        downloadFile(response.data, 'tax_report');
      }

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      filters.setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">Tax Report</h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((item, index) => (
                <Line
                  key={item}
                  activeDot={{ r: 6 }}
                  dataKey={item}
                  dot={{ fill: getChartColor(index), r: 4 }}
                  name="Tax Amount"
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_period`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name="Tax Amount (Previous Period)"
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_year`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name="Tax Amount (Previous Year)"
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('csv')}
            >
              Download CSV
            </Button>
            <Button
              className="ml-2 text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('xlsx')}
            >
              Download XLSX
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
